import { type ComponentType, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
    ArrowTrendingUpIcon,
    BanknotesIcon,
    CheckBadgeIcon,
    CheckCircleIcon,
    ChevronRightIcon,
    ClockIcon,
    CurrencyDollarIcon,
    ExclamationTriangleIcon,
    GlobeAltIcon,
    MapPinIcon,
    ShieldExclamationIcon,
    StarIcon,
    TicketIcon,
    TruckIcon,
    UserGroupIcon,
    WalletIcon,
    WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";

import { cn } from "@/lib/cn";
import { getTripStats, getUserStats, getWalletStats } from "../api/dashboardApi";
import {
    getGeographicAnalytics,
    getOperationalAnalytics,
    getQualityAnalytics,
} from "@/features/reports-analytics/api/analyticsApi";

type Tone = "primary" | "accent" | "emerald" | "amber" | "slate";

type MetricCardProps = {
    label: string;
    value: string;
    helper: string;
    icon: ComponentType<{ className?: string }>;
    tone?: Tone;
    isLoading?: boolean;
    muted?: boolean;
};

type SummaryItemProps = {
    label: string;
    value: string;
    helper?: string;
    isLoading?: boolean;
    muted?: boolean;
};

type SectionCardProps = {
    eyebrow: string;
    title: string;
    description: string;
    linkTo: string;
    linkLabel: string;
    summaryItems: SummaryItemProps[];
    children: ReactNode;
    error?: string | null;
};

const toneClasses: Record<Tone, string> = {
    primary: "bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400",
    accent: "bg-accent-50 text-accent-600 dark:bg-accent-900/20 dark:text-accent-400",
    emerald: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400",
    amber: "bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400",
    slate: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
};

const formatNumber = (value: number) => new Intl.NumberFormat("en-US").format(value);

const formatCurrency = (value: number | null | undefined) => {
    if (value == null || !Number.isFinite(value)) return "—";
    return new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: "NGN",
        maximumFractionDigits: 0,
    }).format(value);
};

const isFiniteNumber = (value: number | null | undefined): value is number =>
    typeof value === "number" && Number.isFinite(value);

const formatPercentage = (value: number | null | undefined, digits = 1) => {
    if (!isFiniteNumber(value)) return "N/A";
    return `${value.toFixed(digits)}%`;
};

const formatAverage = (value: number | null | undefined, digits = 1) => {
    if (!isFiniteNumber(value)) return "N/A";
    return value.toFixed(digits);
};

const formatMinutes = (value: number | null | undefined) => {
    if (!isFiniteNumber(value)) return "N/A";
    return `${value.toFixed(1)} min`;
};

const formatHours = (value: number | null | undefined) => {
    if (!isFiniteNumber(value)) return "N/A";
    return `${value.toFixed(1)} hrs`;
};

const formatShare = (value: number, total: number) => {
    if (!total) return "0% of total";
    return `${Math.round((value / total) * 100)}% of total`;
};

const getErrorMessage = (error: unknown) => {
    if (error instanceof Error && error.message) return error.message;
    return "Unable to load this section right now.";
};

const LoadingBlock = ({ className }: { className?: string }) => (
    <div className={cn("animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800", className)} />
);

const MetricCard = ({
    label,
    value,
    helper,
    icon: Icon,
    tone = "slate",
    isLoading = false,
    muted = false,
}: MetricCardProps) => (
    <article className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{label}</p>
                {isLoading ? (
                    <LoadingBlock className="mt-3 h-8 w-24" />
                ) : (
                    <p
                        className={cn(
                            "mt-3 text-3xl font-semibold",
                            muted ? "text-slate-500 dark:text-slate-400" : "text-slate-900 dark:text-slate-50"
                        )}
                    >
                        {value}
                    </p>
                )}
            </div>
            <div className={cn("flex h-11 w-11 items-center justify-center rounded-xl", toneClasses[tone])}>
                <Icon className="h-5 w-5" />
            </div>
        </div>
        {isLoading ? (
            <LoadingBlock className="mt-4 h-4 w-11/12" />
        ) : (
            <p className="mt-4 text-sm leading-6 text-slate-500 dark:text-slate-400">{helper}</p>
        )}
    </article>
);

const SummaryItem = ({ label, value, helper, isLoading = false, muted = false }: SummaryItemProps) => (
    <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/50">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</p>
        {isLoading ? (
            <LoadingBlock className="mt-2 h-6 w-16" />
        ) : (
            <p
                className={cn(
                    "mt-2 text-lg font-semibold",
                    muted ? "text-slate-500 dark:text-slate-400" : "text-slate-900 dark:text-slate-50"
                )}
            >
                {value}
            </p>
        )}
        {helper ? (
            isLoading ? (
                <LoadingBlock className="mt-2 h-3 w-20" />
            ) : (
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{helper}</p>
            )
        ) : null}
    </div>
);

const SectionErrorState = ({ message }: { message: string }) => (
    <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5 dark:border-rose-900/40 dark:bg-rose-900/10">
        <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 text-rose-500" />
            <div>
                <p className="text-sm font-semibold text-rose-700 dark:text-rose-300">Section unavailable</p>
                <p className="mt-1 text-sm text-rose-600 dark:text-rose-400">{message}</p>
            </div>
        </div>
    </div>
);

const SectionCard = ({
    eyebrow,
    title,
    description,
    linkTo,
    linkLabel,
    summaryItems,
    children,
    error,
}: SectionCardProps) => (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                    {eyebrow}
                </p>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900 dark:text-slate-50">{title}</h2>
                <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">{description}</p>
            </div>

            <Link
                to={linkTo}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
            >
                {linkLabel}
                <ChevronRightIcon className="h-4 w-4" />
            </Link>
        </div>

        {error ? (
            <div className="mt-6">
                <SectionErrorState message={error} />
            </div>
        ) : (
            <>
                <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    {summaryItems.map((item) => (
                        <SummaryItem key={item.label} {...item} />
                    ))}
                </div>
                <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">{children}</div>
            </>
        )}
    </section>
);

export const DashboardOverviewRoute = () => {
    const {
        data: userStatsData,
        isLoading: isLoadingUsers,
        error: userError,
    } = useQuery({
        queryKey: ["userStats"],
        queryFn: () => getUserStats(),
    });

    const {
        data: tripStatsData,
        isLoading: isLoadingTrips,
        error: tripError,
    } = useQuery({
        queryKey: ["tripStats"],
        queryFn: () => getTripStats(),
    });

    const {
        data: walletStatsData,
        isLoading: isLoadingWallet,
        error: walletError,
    } = useQuery({
        queryKey: ["walletStats"],
        queryFn: () => getWalletStats(),
    });

    const {
        data: geographicData,
        isLoading: isLoadingGeographic,
        error: geographicError,
    } = useQuery({
        queryKey: ["analytics-geographic-dashboard"],
        queryFn: () => getGeographicAnalytics(),
    });

    const {
        data: operationalData,
        isLoading: isLoadingOperational,
        error: operationalError,
    } = useQuery({
        queryKey: ["analytics-operational-dashboard"],
        queryFn: () => getOperationalAnalytics(),
    });

    const {
        data: qualityData,
        isLoading: isLoadingQuality,
        error: qualityError,
    } = useQuery({
        queryKey: ["analytics-quality-dashboard"],
        queryFn: () => getQualityAnalytics(),
    });

    const userStats = userStatsData?.data;
    const tripStats = tripStatsData?.data;
    const walletStats = walletStatsData?.data;
    const geographic = geographicData?.data;
    const operational = operationalData?.data;
    const quality = qualityData?.data;

    const totalDrivers = userStats?.totalDrivers ?? 0;
    const activeDrivers = userStats?.activeDrivers ?? 0;
    const inactiveDrivers = userStats?.inactiveDrivers ?? 0;
    const verifiedDrivers = userStats?.verifiedDrivers ?? 0;
    const newDriversWeekToDate = userStats?.newDriversWeekToDate ?? 0;
    const totalRiders = userStats?.totalRiders ?? 0;
    const activeRiders = userStats?.activeRiders ?? 0;
    const inactiveRiders = userStats?.inactiveRiders ?? 0;
    const verifiedRiders = userStats?.verifiedRiders ?? 0;
    const newRidersWeekToDate = userStats?.newRidersWeekToDate ?? 0;

    const totalTrips = tripStats?.totalTrips ?? 0;
    const totalBookings = tripStats?.totalBookings ?? 0;
    const completedTrips = tripStats?.completedTrips ?? 0;
    const pendingTrips = tripStats?.pendingTrips ?? 0;
    const inProgressTrips = tripStats?.inProgressTrips ?? 0;
    const rideMatchRatePct = tripStats?.rideMatchRatePct ?? null;
    const riderSuccessRatePct = tripStats?.riderSuccessRatePct ?? null;
    const averageSeatsFilled = tripStats?.averageSeatsFilled ?? null;
    const seatUtilizationRatePct = tripStats?.seatUtilizationRatePct ?? null;

    const totalWalletFunded = walletStats?.totalWalletFunded ?? walletStats?.totalTopUpVolume ?? 0;
    const platformCommission = walletStats?.platformCommission ?? walletStats?.totalCommissions ?? 0;
    const avgRidePrice = walletStats?.avgRidePrice ?? null;
    const walletBalances = walletStats?.walletBalances ?? walletStats?.totalBalanceInCirculation ?? 0;
    const pendingDriverPayoutAmount = walletStats?.pendingDriverPayoutAmount ?? 0;
    const completedPayoutAmount = walletStats?.completedPayoutAmount ?? walletStats?.totalPayoutAmount ?? 0;
    const totalWalletFundedWtd = walletStats?.totalWalletFundedWtd ?? walletStats?.topUpVolumeLast7Days ?? 0;
    const platformCommissionWtd = walletStats?.platformCommissionWtd ?? walletStats?.commissionsLast7Days ?? 0;
    const totalPayoutWtd = walletStats?.totalPayoutWtd ?? walletStats?.payoutsLast7Days ?? 0;

    const pickupHotspots = geographic?.pickupHotspots ?? geographic?.popularPickupAreas ?? [];
    const dropoffHotspots = geographic?.dropoffHotspots ?? geographic?.popularDropoffAreas ?? [];
    const topRoutes = geographic?.topRoutes ?? geographic?.popularRoutes ?? [];
    const rideDensityAreas = geographic?.rideDensityAreas ?? [];
    const topRoute = topRoutes[0];
    const topPickupHotspot = pickupHotspots[0];
    const topDropoffHotspot = dropoffHotspots[0];
    const topDensityArea = rideDensityAreas[0];

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-50">Operations Overview</h1>
                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                    Six summary sections for users, trips, finance, geography, operations, and service quality.
                </p>
            </div>

            <SectionCard
                eyebrow="User Stats"
                title="Drivers and riders at a glance"
                description="Track supply, demand, verification, and weekly onboarding without leaving the overview."
                linkTo="/reports-analytics/users"
                linkLabel="View user details"
                error={userError ? getErrorMessage(userError) : null}
                summaryItems={[
                    {
                        label: "Inactive Drivers",
                        value: formatNumber(inactiveDrivers),
                        helper: formatShare(inactiveDrivers, totalDrivers),
                        isLoading: isLoadingUsers,
                    },
                    {
                        label: "Inactive Riders",
                        value: formatNumber(inactiveRiders),
                        helper: formatShare(inactiveRiders, totalRiders),
                        isLoading: isLoadingUsers,
                    },
                    {
                        label: "Verified Accounts",
                        value: formatNumber(verifiedDrivers + verifiedRiders),
                        helper: "drivers and riders",
                        isLoading: isLoadingUsers,
                    },
                    {
                        label: "New This Week",
                        value: formatNumber(newDriversWeekToDate + newRidersWeekToDate),
                        helper: "since Monday 00:00",
                        isLoading: isLoadingUsers,
                    },
                ]}
            >
                <MetricCard
                    label="Total Drivers"
                    value={formatNumber(totalDrivers)}
                    helper={`${formatNumber(verifiedDrivers)} verified driver accounts`}
                    icon={TruckIcon}
                    tone="primary"
                    isLoading={isLoadingUsers}
                />
                <MetricCard
                    label="Active Drivers"
                    value={formatNumber(activeDrivers)}
                    helper={`${formatShare(activeDrivers, totalDrivers)} with a ride offer in the last 2 weeks`}
                    icon={ArrowTrendingUpIcon}
                    tone="emerald"
                    isLoading={isLoadingUsers}
                />
                <MetricCard
                    label="Total Riders"
                    value={formatNumber(totalRiders)}
                    helper={`${formatNumber(verifiedRiders)} verified rider accounts`}
                    icon={UserGroupIcon}
                    tone="accent"
                    isLoading={isLoadingUsers}
                />
                <MetricCard
                    label="Active Riders"
                    value={formatNumber(activeRiders)}
                    helper={`${formatShare(activeRiders, totalRiders)} booked in the last 2 weeks`}
                    icon={CheckBadgeIcon}
                    tone="emerald"
                    isLoading={isLoadingUsers}
                />
            </SectionCard>

            <SectionCard
                eyebrow="Trips Stats"
                title="Trip volume and marketplace movement"
                description="See request volume, trip progress, and the current marketplace conversion proxies in one place."
                linkTo="/reports-analytics/trips"
                linkLabel="View trip details"
                error={tripError ? getErrorMessage(tripError) : null}
                summaryItems={[
                    {
                        label: "Pending Trips",
                        value: formatNumber(pendingTrips),
                        helper: "created but not started",
                        isLoading: isLoadingTrips,
                    },
                    {
                        label: "In-progress Trips",
                        value: formatNumber(inProgressTrips),
                        helper: "currently active on the road",
                        isLoading: isLoadingTrips,
                    },
                    {
                        label: "Ride Match Rate",
                        value: formatPercentage(rideMatchRatePct),
                        helper: "current conversion proxy",
                        isLoading: isLoadingTrips,
                    },
                    {
                        label: "Average Seats Filled",
                        value: formatAverage(averageSeatsFilled),
                        helper: "per ride offer",
                        isLoading: isLoadingTrips,
                    },
                ]}
            >
                <MetricCard
                    label="Total Trips"
                    value={formatNumber(totalTrips)}
                    helper="All trip records"
                    icon={MapPinIcon}
                    tone="slate"
                    isLoading={isLoadingTrips}
                />
                <MetricCard
                    label="Bookings"
                    value={formatNumber(totalBookings)}
                    helper="All seat booking records"
                    icon={TicketIcon}
                    tone="primary"
                    isLoading={isLoadingTrips}
                />
                <MetricCard
                    label="Completed Trips"
                    value={formatNumber(completedTrips)}
                    helper={`${formatShare(completedTrips, totalTrips)} of all trips`}
                    icon={CheckCircleIcon}
                    tone="emerald"
                    isLoading={isLoadingTrips}
                />
                <MetricCard
                    label="Seat Utilization Rate"
                    value={formatPercentage(seatUtilizationRatePct)}
                    helper="Booked seats versus offered seats"
                    icon={UserGroupIcon}
                    tone="amber"
                    isLoading={isLoadingTrips}
                />
            </SectionCard>

            <SectionCard
                eyebrow="Financial Stats"
                title="Wallet funding, earnings, balances, and payouts"
                description="Monitor cash moving through the platform, current wallet balances, and what is owed out to drivers."
                linkTo="/reports-analytics/financial"
                linkLabel="View financial details"
                error={walletError ? getErrorMessage(walletError) : null}
                summaryItems={[
                    {
                        label: "Wallet Funded WTD",
                        value: formatCurrency(totalWalletFundedWtd),
                        helper: "current week",
                        isLoading: isLoadingWallet,
                    },
                    {
                        label: "Commission WTD",
                        value: formatCurrency(platformCommissionWtd),
                        helper: "current week",
                        isLoading: isLoadingWallet,
                    },
                    {
                        label: "Payout WTD",
                        value: formatCurrency(totalPayoutWtd),
                        helper: "current week",
                        isLoading: isLoadingWallet,
                    },
                    {
                        label: "Avg Ride Price",
                        value: formatCurrency(avgRidePrice),
                        helper: "confirmed bookings",
                        isLoading: isLoadingWallet,
                        muted: !isFiniteNumber(avgRidePrice),
                    },
                ]}
            >
                <MetricCard
                    label="Total Wallet Funded"
                    value={formatCurrency(totalWalletFunded)}
                    helper="Completed top-up wallet transactions"
                    icon={WalletIcon}
                    tone="primary"
                    isLoading={isLoadingWallet}
                />
                <MetricCard
                    label="Platform Commission"
                    value={formatCurrency(platformCommission)}
                    helper="Commission already posted to wallet transactions"
                    icon={CurrencyDollarIcon}
                    tone="emerald"
                    isLoading={isLoadingWallet}
                />
                <MetricCard
                    label="Wallet Balances"
                    value={formatCurrency(walletBalances)}
                    helper="Money currently sitting in active wallets"
                    icon={BanknotesIcon}
                    tone="accent"
                    isLoading={isLoadingWallet}
                />
                <MetricCard
                    label="Pending Driver Payouts"
                    value={formatCurrency(pendingDriverPayoutAmount)}
                    helper={`${formatCurrency(completedPayoutAmount)} already completed`}
                    icon={TicketIcon}
                    tone="amber"
                    isLoading={isLoadingWallet}
                />
            </SectionCard>

            <SectionCard
                eyebrow="Geographic Analytics"
                title="Routes, hotspots, and ride density"
                description="Use trip-backed location data to see where activity is concentrated and which corridors repeat most often."
                linkTo="/reports-analytics/geographic"
                linkLabel="View geographic details"
                error={geographicError ? getErrorMessage(geographicError) : null}
                summaryItems={[
                    {
                        label: "Top Routes",
                        value: formatNumber(topRoutes.length),
                        helper: "ranked trip corridors",
                        isLoading: isLoadingGeographic,
                    },
                    {
                        label: "Pickup Hotspots",
                        value: formatNumber(pickupHotspots.length),
                        helper: "ranked pickup zones",
                        isLoading: isLoadingGeographic,
                    },
                    {
                        label: "Dropoff Hotspots",
                        value: formatNumber(dropoffHotspots.length),
                        helper: "ranked destination zones",
                        isLoading: isLoadingGeographic,
                    },
                    {
                        label: "Ride Density Areas",
                        value: formatNumber(rideDensityAreas.length),
                        helper: "combined activity grid cells",
                        isLoading: isLoadingGeographic,
                    },
                ]}
            >
                <MetricCard
                    label="Top Route"
                    value={topRoute ? formatNumber(topRoute.tripCount) : "—"}
                    helper={topRoute ? `${topRoute.origin} to ${topRoute.destination}` : "No route data yet"}
                    icon={MapPinIcon}
                    tone="primary"
                    isLoading={isLoadingGeographic}
                    muted={!topRoute}
                />
                <MetricCard
                    label="Pickup Hotspot"
                    value={topPickupHotspot ? formatNumber(topPickupHotspot.tripCount) : "—"}
                    helper={topPickupHotspot ? topPickupHotspot.name : "No pickup hotspot data yet"}
                    icon={ArrowTrendingUpIcon}
                    tone="emerald"
                    isLoading={isLoadingGeographic}
                    muted={!topPickupHotspot}
                />
                <MetricCard
                    label="Dropoff Hotspot"
                    value={topDropoffHotspot ? formatNumber(topDropoffHotspot.tripCount) : "—"}
                    helper={topDropoffHotspot ? topDropoffHotspot.name : "No dropoff hotspot data yet"}
                    icon={GlobeAltIcon}
                    tone="accent"
                    isLoading={isLoadingGeographic}
                    muted={!topDropoffHotspot}
                />
                <MetricCard
                    label="Busiest Density Area"
                    value={topDensityArea ? formatNumber(topDensityArea.tripCount) : "—"}
                    helper={topDensityArea ? topDensityArea.name : "No density data yet"}
                    icon={UserGroupIcon}
                    tone="amber"
                    isLoading={isLoadingGeographic}
                    muted={!topDensityArea}
                />
            </SectionCard>

            <SectionCard
                eyebrow="Operational Analytics"
                title="Support workload and platform health"
                description="Keep an eye on support responsiveness, payment friction, and the current system-health proxy behind operational alerts."
                linkTo="/reports-analytics/operational"
                linkLabel="View operational details"
                error={operationalError ? getErrorMessage(operationalError) : null}
                summaryItems={[
                    {
                        label: "Open Support Tickets",
                        value: formatNumber(operational?.openSupportTickets ?? 0),
                        helper: "current backlog",
                        isLoading: isLoadingOperational,
                    },
                    {
                        label: "Payment Failures",
                        value: formatNumber(operational?.paymentFailures ?? 0),
                        helper: "failed payments in period",
                        isLoading: isLoadingOperational,
                    },
                    {
                        label: "App Errors",
                        value: formatNumber(operational?.appErrors ?? 0),
                        helper: "current operational proxy",
                        isLoading: isLoadingOperational,
                    },
                    {
                        label: "Open Alerts",
                        value: formatNumber(operational?.openOperationalAlerts ?? 0),
                        helper: "unresolved incidents",
                        isLoading: isLoadingOperational,
                    },
                ]}
            >
                <MetricCard
                    label="Support Tickets"
                    value={formatNumber(operational?.supportTickets ?? 0)}
                    helper={`${formatNumber(operational?.resolvedSupportTickets ?? 0)} resolved in period`}
                    icon={ShieldExclamationIcon}
                    tone="primary"
                    isLoading={isLoadingOperational}
                />
                <MetricCard
                    label="Resolution Time"
                    value={formatHours(operational?.averageResolutionTimeHours)}
                    helper="Average time from open to resolution"
                    icon={ClockIcon}
                    tone="emerald"
                    isLoading={isLoadingOperational}
                    muted={!isFiniteNumber(operational?.averageResolutionTimeHours)}
                />
                <MetricCard
                    label="Failed Payment Amount"
                    value={formatCurrency(operational?.paymentFailureAmount)}
                    helper={`${formatNumber(operational?.paymentFailures ?? 0)} failed payments`}
                    icon={CurrencyDollarIcon}
                    tone="accent"
                    isLoading={isLoadingOperational}
                />
                <MetricCard
                    label="Webhook and Dispatch Failures"
                    value={formatNumber(
                        (operational?.webhookFailures ?? 0) + (operational?.gatewayDispatchFailures ?? 0)
                    )}
                    helper="Combined gateway failure proxies"
                    icon={WrenchScrewdriverIcon}
                    tone="amber"
                    isLoading={isLoadingOperational}
                />
            </SectionCard>

            <SectionCard
                eyebrow="Quality and Experience Analytics"
                title="Reliability, ratings, and rider experience"
                description="Track trip quality, ratings coverage, no-shows, and timing metrics that shape the rider and driver experience."
                linkTo="/reports-analytics/quality"
                linkLabel="View quality details"
                error={qualityError ? getErrorMessage(qualityError) : null}
                summaryItems={[
                    {
                        label: "Completion Rate",
                        value: formatPercentage(quality?.completionRatePct),
                        helper: "top-level reliability",
                        isLoading: isLoadingQuality,
                    },
                    {
                        label: "Cancellation Rate",
                        value: formatPercentage(quality?.cancellationRatePct),
                        helper: "trip reliability drag",
                        isLoading: isLoadingQuality,
                    },
                    {
                        label: "No-show Rate",
                        value: formatPercentage(quality?.noShowRatePct),
                        helper: `${formatNumber(quality?.noShowBookings ?? 0)} no-show bookings`,
                        isLoading: isLoadingQuality,
                    },
                    {
                        label: "On-time Arrival",
                        value: formatPercentage(quality?.onTimeArrivalRatePct),
                        helper: "arrival punctuality proxy",
                        isLoading: isLoadingQuality,
                    },
                ]}
            >
                <MetricCard
                    label="Average Driver Rating"
                    value={formatAverage(quality?.averageDriverRating)}
                    helper={`${formatPercentage(quality?.driverRatingCoveragePct)} coverage`}
                    icon={StarIcon}
                    tone="primary"
                    isLoading={isLoadingQuality}
                    muted={!isFiniteNumber(quality?.averageDriverRating)}
                />
                <MetricCard
                    label="Average Passenger Rating"
                    value={formatAverage(quality?.averagePassengerRating)}
                    helper={`${formatPercentage(quality?.passengerRatingCoveragePct)} coverage`}
                    icon={CheckBadgeIcon}
                    tone="accent"
                    isLoading={isLoadingQuality}
                    muted={!isFiniteNumber(quality?.averagePassengerRating)}
                />
                <MetricCard
                    label="Average Pickup Wait"
                    value={formatMinutes(quality?.averagePickupWaitMinutes)}
                    helper="Curbside wait once the driver arrives"
                    icon={ClockIcon}
                    tone="amber"
                    isLoading={isLoadingQuality}
                    muted={!isFiniteNumber(quality?.averagePickupWaitMinutes)}
                />
                <MetricCard
                    label="Average Trip Duration"
                    value={formatMinutes(quality?.averageTripDurationMinutes)}
                    helper={`${formatMinutes(quality?.averageArrivalDelayMinutes)} average arrival delay`}
                    icon={MapPinIcon}
                    tone="emerald"
                    isLoading={isLoadingQuality}
                    muted={!isFiniteNumber(quality?.averageTripDurationMinutes)}
                />
            </SectionCard>
        </div>
    );
};
